import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema } from './common.js';
import { HotelRoomOccupancyInputSchema } from './hotel-booking.js';
import { TboFlightCabinClassSchema, TboFlightPassengerTypeSchema } from './tbo-flight.js';

/**
 * Recent searches — the strip of "pick up where you left off" cards under the
 * home search widget.
 *
 * Stored server-side rather than in the browser, because the value of the
 * feature is that it follows the traveller: a stay looked up on a phone at
 * lunchtime should still be one tap away on a laptop that evening. Rows are
 * owned by a `sessionId` the browser mints and, once the visitor signs in, by
 * their customer id as well — see `RecentSearchesService`.
 *
 * The criteria are stored **denormalised**, city labels and all. A card has to
 * render before anything else is fetched, and re-resolving three IATA codes
 * against the City master to draw one line of text is a lookup per card on
 * every page load. Storing the label alongside the code also means a card keeps
 * reading the way it read when it was searched, even if the master is later
 * renamed.
 */

/** Calendar date as YYYY-MM-DD. */
const CalendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')
  .describe('Calendar date YYYY-MM-DD');

export const RecentSearchProductSchema = z.enum(['FLIGHT', 'HOTEL']);
export type RecentSearchProduct = z.infer<typeof RecentSearchProductSchema>;

export const RecentSearchTripTypeSchema = z.enum(['ONE_WAY', 'ROUND_TRIP', 'MULTI_CITY']);
export type RecentSearchTripType = z.infer<typeof RecentSearchTripTypeSchema>;

/**
 * One leg of a remembered flight search.
 *
 * `from` / `to` are the identity — they are what gets re-searched. The two
 * labels are display only and are allowed to be blank: a visitor can type a
 * code straight into the field without ever opening the dropdown, in which case
 * there is no city name to remember, and the card falls back to the code.
 */
export const RecentFlightSearchLegSchema = z.object({
  from: z.string().trim().length(3).toUpperCase(),
  to: z.string().trim().length(3).toUpperCase(),
  date: CalendarDateSchema,
  /** City name for `from`, e.g. "Delhi". Display only. */
  fromLabel: z.string().trim().max(120).default(''),
  /** City name for `to`, e.g. "London". Display only. */
  toLabel: z.string().trim().max(120).default(''),
});
export type RecentFlightSearchLeg = z.infer<typeof RecentFlightSearchLegSchema>;

/**
 * A remembered flight search, in the shape the widget collects rather than the
 * shape TBO wants.
 *
 * Deliberately mirrors the panel: a round trip keeps its `returnDate` as a
 * separate field instead of being flattened into a mirrored second leg, so
 * re-opening the widget from a card restores the form the visitor actually
 * filled in. The flattening happens later, when the search URL is built.
 */
export const RecentFlightSearchSchema = z.object({
  trip: RecentSearchTripTypeSchema,
  legs: z.array(RecentFlightSearchLegSchema).min(1).max(6),
  /** Set for ROUND_TRIP only. */
  returnDate: CalendarDateSchema.nullable().default(null),
  adults: z.number().int().min(1).max(9),
  children: z.number().int().min(0).max(9).default(0),
  infants: z.number().int().min(0).max(9).default(0),
  cabinClass: TboFlightCabinClassSchema.default('ECONOMY'),
  /** Special-fare concession, or null for regular fares. */
  passengerType: TboFlightPassengerTypeSchema.nullable().default(null),
  /**
   * Both cities are in India, so the results page splits a round trip into two
   * one-way calls. Carried because the flag is derived from country codes the
   * card no longer has to hand, and re-deriving it would mean an airport lookup
   * before the search could be replayed.
   */
  domestic: z.boolean().default(false),
});
export type RecentFlightSearch = z.infer<typeof RecentFlightSearchSchema>;

/** A remembered stay search. Mirrors `StayCriteria` on the website. */
export const RecentHotelSearchSchema = z
  .object({
    /** TBO `CityId`. Not an IATA code — stays and flights use different masters. */
    cityId: z.string().trim().min(1).max(40),
    /** Display label, e.g. "Goa, India". */
    cityName: z.string().trim().min(1).max(160),
    checkIn: CalendarDateSchema,
    checkOut: CalendarDateSchema,
    rooms: z.array(HotelRoomOccupancyInputSchema).min(1).max(8),
    guestNationality: z.string().trim().length(2).default('IN'),
    currencyCode: z.string().trim().length(3).default('INR'),
  })
  .refine((v) => v.checkOut > v.checkIn, {
    message: 'Check-out must be after check-in',
    path: ['checkOut'],
  });
export type RecentHotelSearch = z.infer<typeof RecentHotelSearchSchema>;

/**
 * What the widget posts after a successful search.
 *
 * A discriminated union rather than two nullable fields, so "product is FLIGHT
 * but the flight criteria are missing" cannot be expressed, let alone stored.
 */
export const RecentSearchCreateRequestSchema = z.discriminatedUnion('product', [
  z.object({ product: z.literal('FLIGHT'), flight: RecentFlightSearchSchema }),
  z.object({ product: z.literal('HOTEL'), hotel: RecentHotelSearchSchema }),
]);
export type RecentSearchCreateRequest = z.infer<typeof RecentSearchCreateRequestSchema>;

const RecentSearchBaseSchema = z.object({
  id: UuidSchema,
  /** Last time these exact criteria were searched, not when first seen. */
  searchedAt: IsoDateTimeSchema,
});

export const RecentSearchItemSchema = z.discriminatedUnion('product', [
  RecentSearchBaseSchema.extend({
    product: z.literal('FLIGHT'),
    flight: RecentFlightSearchSchema,
  }),
  RecentSearchBaseSchema.extend({
    product: z.literal('HOTEL'),
    hotel: RecentHotelSearchSchema,
  }),
]);
export type RecentSearchItem = z.infer<typeof RecentSearchItemSchema>;

export const RecentSearchListQuerySchema = z.object({
  /** Narrow to one product. Omit for both, newest first. */
  product: RecentSearchProductSchema.optional(),
  limit: z.coerce.number().int().min(1).max(20).optional().default(6),
});
export type RecentSearchListQuery = z.infer<typeof RecentSearchListQuerySchema>;

export const RecentSearchListResponseSchema = z.object({
  data: z.array(RecentSearchItemSchema),
});
export type RecentSearchListResponse = z.infer<typeof RecentSearchListResponseSchema>;
